import { ref, computed, onMounted, onBeforeUnmount, provide } from 'vue'
import { useStore } from '@nanostores/vue'
import { DialogType } from '@/types/DialogLegacy'
import {
	setPostDetails,
	getProductSharingUrl,
	checkExpressCheckoutEnable,
} from '@/legacy/ts/productSharing'
import useProductSharing from '@/legacy/ts/useProductSharing'
import {
	exchangeLocalPrice,
	getCurrencySymbolOriginal,
} from '@/assets/ts/currency'
import {
	fetchSettingSupportedCurrencies,
	fetchSettingWarehouses,
	instanceProductSharing,
	instanceBuyandship,
	fetchSettingBuyforyou as fetchSettingBuyforyouApi,
} from '@/assets/ts/apiManagement'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'
import { fetchPricingCurrencyBuyforyou } from '@/legacy/ts/buyforyou'
import goErrorPage from '@/legacy/ts/goErrorPage'
import {
	fromPostId,
	setFromPostId,
	bnsRatesStore,
	fetchBNSRatesOnce,
} from '@/store/productSharingStore'
import { getMcrRootUrl, getSiteBaseUrl } from '@/lib/siteBaseUrl'

const fromPostIdGlobal = useStore(fromPostId)
export default function usePostCard(refPostlist: any) {
	const isShowDialogPostDetail = ref(false)
	const isLoadingPostDetail = ref(true)
	const currentPostId = ref('')
	const b4uRates = ref<Record<string, number> | null>(null)
	const bnsRates = useStore(bnsRatesStore)
	const categoryMap = ref<any>(null)
	const warehouseIdMap = ref(null)
	const interestsMap = ref<any>(null)
	const supportedCurrencies = ref(null)
	const buyforyouSupportWarehouse = ref<string[] | null>(null)
	const currencySymbolOriginal = ref('')
	const priceLocal = ref('')
	const priceLocalPost = ref('')
	const interestsList = ref<any>([])
	const localCurrencyInfo = ref<Awaited<
		ReturnType<typeof fetchPricingCurrencyBuyforyou>
	> | null>(null)
	const currencyData = ref<Awaited<
		ReturnType<typeof fetchPricingCurrencyBuyforyou>
	> | null>(null)
	const isExpressCheckoutEnable = ref(checkExpressCheckoutEnable())
	const { PSUsername, connectPS } = useProductSharing()
	const currentPageOriginalHref = ref(window.location.href)
	const postDetailCurrent = ref<any>(null)
	const postListId = ref<string[]>([])

	const locale = computed(() =>
		import.meta.env.DEV ? window.BUYANDSHIP.settings.current_language : '',
	)
	const isSupportExpressCheckout = computed(
		() =>
			(postDetailCurrent.value &&
				postDetailCurrent.value.isExpressCheckout &&
				isExpressCheckoutEnable.value) ||
			false,
	)

	function resetPost() {
		currentPostId.value = ''
		isLoadingPostDetail.value = true
	}

	function setPostActions(payload: any) {
		if (
			!payload ||
			!postDetailCurrent.value ||
			payload.id !== postDetailCurrent.value.id
		)
			return
		postDetailCurrent.value[payload.param] = payload.val
		if (payload.likeCount !== undefined) {
			postDetailCurrent.value.likeCount = payload.likeCount
		}
	}

	function setBrowserUrl() {
		window.history.pushState(
			{ id: currentPostId.value },
			'',
			getProductSharingUrl('post', {
				id: currentPostId.value,
				fromPostId: fromPostIdGlobal.value,
			}),
		)
	}

	function hideDialogPostDetail(from = '') {
		if (from === 'dialog') {
			if (postListId.value.length) {
				window.history.pushState({ id: '' }, '', currentPageOriginalHref.value)
			} else {
				if (window.history.state?.disableReplaceState) return
				window.history.replaceState(
					{ id: '' },
					'',
					currentPageOriginalHref.value,
				)
			}
		}
		setFromPostId('')
		isShowDialogPostDetail.value = false
		window.BsHeaderVm.setTypeSuccessDialog('bns')
	}

	function handlePopstate(event: any) {
		if (event.state && event.state.id) {
			postItemClick({
				postId: event.state.id,
				isFromClick: false,
			})
		} else {
			postListId.value = []
			hideDialogPostDetail('back')
		}
	}

	async function fetchSettingInterest() {
		if (interestsMap.value && interestsList.value.length)
			return Promise.resolve()
		const { data } = await instanceBuyandship.get(
			`settings/${locale.value}?k[]=shipment_interests`,
		)
		interestsMap.value = data.shipment_interests
		if (interestsMap.value) {
			interestsList.value = Object.keys(interestsMap.value).map((key: any) => ({
				link: import.meta.env.PROD
					? `${getSiteBaseUrl()}/sharing/explore/interests/${key}/`
					: `product-sharing-index?interest=${key}`,
				name: interestsMap.value[key].name,
				slug: key,
			}))
		}
	}

	function fetchSettingShipmentCategory() {
		if (categoryMap.value) return Promise.resolve()
		return new Promise((resolve, reject) => {
			instanceBuyandship
				.get(`settings/${locale.value}?k[]=shipment_categories`)
				.then(({ data }) => {
					const list = data.shipment_categories.map((v: any) => v[2])
					categoryMap.value = [].concat(...list)
					resolve(true)
				})
				.catch(reject)
		})
	}

	async function fetchSettingBuyforyou() {
		if (buyforyouSupportWarehouse.value) return
		try {
			const result = await fetchSettingBuyforyouApi()
			buyforyouSupportWarehouse.value = result.formattedBuyforyouWarehouses
		} catch (error) {
			console.error('Failed to fetch setting: ', error)
			throw error
		}
	}

	async function fetchPostDetails() {
		try {
			const {
				data: { data },
			} = await instanceProductSharing.get(`/posts/${currentPostId.value}`)
			if (data.user.username === PSUsername.value) {
				window.location.href = `${getMcrRootUrl()}/account/productsharing/posts/${
					data.id
				}`
				return
			}
			const postDetail = setPostDetails(data)
			postDetailCurrent.value = postDetail
			postListId.value.push(postDetail.id)

			createGAEvent(GaGeneralEvent.PageLoad, {
				page_name: GaGeneralPageName.CommunityPostDetailPage,
				page_object_id: `post_${postDetail.id}`,
				post_id: postDetail.id,
				product_id: postDetail.productSlug,
				is_express_checkout: postDetail.isExpressCheckout,
			})
		} catch (error: any) {
			if (error.response && error.response.status === 404) {
				goErrorPage()
			}
			if (error.response && error.response.status === 401) {
				connectPS().then(() => fetchPostDetails())
			}
		}
	}

	async function setWarehouseIdMap() {
		if (warehouseIdMap.value) return Promise.resolve()
		warehouseIdMap.value = await fetchSettingWarehouses()
	}

	async function setSupportedCurrencies() {
		if (supportedCurrencies.value) return Promise.resolve()
		supportedCurrencies.value = await fetchSettingSupportedCurrencies()
	}

	async function fetchCurrencyData() {
		try {
			currencyData.value = await fetchPricingCurrencyBuyforyou()
		} catch (error) {
			currencyData.value = null
		}
	}

	function setB4URates() {
		if (!currencyData.value) return

		// Transform array to b4uRates object, keeping first occurrence of duplicate keys like APP's behavior
		b4uRates.value = currencyData.value.reduce(
			(acc: { [key: string]: number }, item: any) => {
				if (!acc[item.key]) {
					acc[item.key] = item.rate
				}
				return acc
			},
			{},
		)
	}

	async function setCurrencySymbolOriginal() {
		currencySymbolOriginal.value = getCurrencySymbolOriginal({
			warehouse: postDetailCurrent.value.itemInfo.warehouse,
			warehouseIdMap: warehouseIdMap.value,
			supportedCurrencies: supportedCurrencies.value,
		})
	}

	async function setPriceLocal() {
		const priceOriginal = postDetailCurrent.value.isExpressCheckout
			? postDetailCurrent.value.itemInfo.ecPrice
			: postDetailCurrent.value.itemInfo.unitPrice

		const hasB4uRate =
			b4uRates.value?.[postDetailCurrent.value.itemInfo.warehouse] != null
		const rates = hasB4uRate ? b4uRates.value : bnsRates.value
		const floatMax = hasB4uRate
			? currencyData.value?.find(
					(item: any) =>
						item.key === postDetailCurrent.value.itemInfo.warehouse,
			  )?.currencyDecimal || 0
			: 0

		priceLocal.value = `≈ ${exchangeLocalPrice({
			rates,
			price: priceOriginal,
			warehouse: postDetailCurrent.value.itemInfo.warehouse,
			count: 1,
		})}`
		priceLocalPost.value = `≈ ${exchangeLocalPrice({
			rates,
			price: postDetailCurrent.value.itemInfo.unitPrice,
			warehouse: postDetailCurrent.value.itemInfo.warehouse,
			count: 1,
			floatMax,
		})}`
	}

	function setPricingInfo() {
		localCurrencyInfo.value = currencyData.value
	}

	async function postItemClick({
		postId,
		isFromClick = true,
	}: {
		postId: string
		isFromClick: boolean
	}) {
		if (isFromClick) {
			if (isShowDialogPostDetail.value) {
				setFromPostId(currentPostId.value)
			}
		}
		resetPost()
		isShowDialogPostDetail.value = true
		currentPostId.value = postId
		if (isFromClick) {
			setBrowserUrl()
		}
		await Promise.all([
			fetchSettingInterest(),
			fetchSettingShipmentCategory(),
			fetchSettingBuyforyou(),
			setWarehouseIdMap(),
			setSupportedCurrencies(),
			fetchCurrencyData(),
			fetchBNSRatesOnce(),
		])
		await fetchPostDetails()
		setB4URates()
		if (isSupportExpressCheckout.value) {
			setPricingInfo()
		}
		await setPriceLocal()

		await setCurrencySymbolOriginal()
		if (isFromClick) {
			sendPostEvent()
		}
		isLoadingPostDetail.value = false
	}

	async function fetchPricingInfo() {
		try {
			localCurrencyInfo.value = await fetchPricingCurrencyBuyforyou()
		} catch (error) {
			localCurrencyInfo.value = null
		}
	}

	function setPostListInfo(payload: any) {
		refPostlist.value.setPostListAction(payload)
	}

	async function sendPostEvent() {
		return instanceProductSharing
			.post('/events', {
				event_type: 'view',
				post_id: currentPostId.value,
			})
			.then(res => res)
			.catch(error => {
				throw error
			})
	}

	provide('postItemClickProvide', postItemClick)
	provide('setPostListInfoProvide', setPostListInfo)

	onMounted(() => {
		window.addEventListener('popstate', handlePopstate)
		window.onbeforeunload = () => {
			window.history.replaceState(null, '', currentPageOriginalHref.value)
		}
	})

	onBeforeUnmount(() => {
		window.removeEventListener('popstate', handlePopstate)
	})

	return {
		DialogType,
		isShowDialogPostDetail,
		isLoadingPostDetail,
		currentPostId,
		b4uRates,
		categoryMap,
		warehouseIdMap,
		interestsMap,
		supportedCurrencies,
		buyforyouSupportWarehouse,
		currencySymbolOriginal,
		priceLocal,
		priceLocalPost,
		interestsList,
		localCurrencyInfo,
		isSupportExpressCheckout,
		postDetailCurrent,
		postItemClick,
		hideDialogPostDetail,
		setPostActions,
		fetchPricingInfo,
		fetchSettingInterest,
		setSupportedCurrencies,
	}
}
